import React, { useContext } from 'react'
import { FormattedMessage } from 'react-intl'

import styled from 'styled-components/native'

import {
  MENTAL_HEALTH_SESSIONS_VALUE_PROPS,
  VALUE_PROPS_TITLES,
  VALUE_PROPS_TITLES_GOOGLE_CRITICAL_TALENT,
  VALUE_PROPS_TITLES_LYRA_SELECT,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/valuePropsMessages'
import { CUSTOMER_CONFIGURATION } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'

import { CheckMarkIcon } from '../../atoms/icons/CheckMarkIcon'
import { LyraSelectCareNavigatorValuePropSpotIllustration } from '../../atoms/illustrations/LyraSelectCareNavigatorValuePropSpotIllustration'
import { LyraSelectCareSessionIllustration } from '../../atoms/illustrations/LyraSelectCareSessionIllustration'
import { LyraSelectPersonalizedCareIllustration } from '../../atoms/illustrations/LyraSelectPersonalizedCareIllustration'
import { LyraSelectSelfCareValueSpotIllustration } from '../../atoms/illustrations/LyraSelectSelfCareValueSpotIllustration'
import { ValuePropDedicatedSupport } from '../../atoms/illustrations/ValuePropDedicatedSupport.web'
import { ValuePropMeditationsCoursesArticles } from '../../atoms/illustrations/ValuePropMeditationsCoursesArticles.web'
import { ValuePropMentalHealthSessions } from '../../atoms/illustrations/ValuePropMentalHealthSessions.web'
import { ValuePropOnDemand } from '../../atoms/illustrations/ValuePropOnDemand.web'
import { ValuePropProvidersIntelligentlyMatched } from '../../atoms/illustrations/ValuePropProvidersIntelligentlyMatched.web'
import { Subhead } from '../../atoms/subhead/Subhead'
import { AppContext } from '../../context'
import { useMediaQuerySize } from '../../hooks/useMediaQuerySize'
import { ValuePropsIconList, ValuePropsItemType } from '../../molecules/valuePropsIconList/ValuePropsIconList'
import { SubheadSize } from '../../styles/typeStyles'
import { ProductTypeContext } from '../../utils/productType/productTypeContext'
import { ThemeType } from '../../utils/themes/ThemeProvider'

const ValuePropsHeader = styled(Subhead)<{ theme: ThemeType }>(({ theme: { breakpoints, spacing } }) => ({
  marginBottom: spacing['32px'],
  textAlign: breakpoints.isMinWidthLaptop ? undefined : 'center',
}))

const getAutoEnrollmentMentalHealthCopy = (
  customerConfiguration?: string,
  dualSessionEntitlementsEnabled?: boolean,
  isCustomerDynamicSessionLimitEnabled?: boolean,
) => {
  if (dualSessionEntitlementsEnabled || isCustomerDynamicSessionLimitEnabled) {
    return MENTAL_HEALTH_SESSIONS_VALUE_PROPS.defaultContent
  }

  switch (customerConfiguration) {
    case CUSTOMER_CONFIGURATION.EAP_ONLY:
    case CUSTOMER_CONFIGURATION.EAP_PLUS_HPI:
      return MENTAL_HEALTH_SESSIONS_VALUE_PROPS.sessionCountContent
    case CUSTOMER_CONFIGURATION.HPI_ONLY:
    default:
      return MENTAL_HEALTH_SESSIONS_VALUE_PROPS.defaultContent
  }
}

const getMentalHealthValueProp = (
  isCondensedVersion: boolean,
  hideDependentField: boolean,
  isAutoEnrollment?: boolean,
  customerConfiguration?: string,
  customerDisplayName?: string,
  numberOfSessions?: string | number,
  dualSessionEntitlementsEnabled?: boolean,
  isCustomerDynamicSessionLimitEnabled?: boolean,
) => {
  let title = undefined
  let values = undefined

  if (isAutoEnrollment) {
    const dependentsEnabled = !hideDependentField && !isCustomerDynamicSessionLimitEnabled ? 'yes' : 'other'

    title = getAutoEnrollmentMentalHealthCopy(
      customerConfiguration,
      dualSessionEntitlementsEnabled,
      isCustomerDynamicSessionLimitEnabled,
    )
    values = {
      numberOfSessions: numberOfSessions || '',
      customerDisplayName: customerDisplayName || '',
      dependentsEnabled,
    }
  } else {
    title =
      isCondensedVersion || hideDependentField
        ? VALUE_PROPS_TITLES.condensedMentalHealthSessions
        : VALUE_PROPS_TITLES.mentalHealthSessions
  }

  return { title, values }
}

const getValuePropsItems = (
  isCondensedVersion: boolean,
  hideDependentField: boolean,
  libraryDisabled: boolean,
  isUserGoogleCriticalTalent: boolean,
  isAutoEnrollment?: boolean,
  customerConfiguration?: string,
  customerDisplayName?: string,
  numberOfSessions?: string | number,
  dualSessionEntitlementsEnabled?: boolean,
): ValuePropsItemType[] => {
  const { isLyraSelect } = useContext(ProductTypeContext)
  const { isCustomerDynamicSessionLimitEnabled } = useContext(AppContext)

  const mentalHealthValueProp = getMentalHealthValueProp(
    isCondensedVersion,
    hideDependentField,
    isAutoEnrollment,
    customerConfiguration,
    customerDisplayName,
    numberOfSessions,
    dualSessionEntitlementsEnabled,
    isCustomerDynamicSessionLimitEnabled,
  )

  const wfmhValueProps = [
    {
      icon: isCondensedVersion ? <CheckMarkIcon /> : <ValuePropMentalHealthSessions />,
      title: mentalHealthValueProp.title,
      values: mentalHealthValueProp.values,
    },
    ...(libraryDisabled
      ? []
      : [
          {
            icon: isCondensedVersion ? <CheckMarkIcon /> : <ValuePropMeditationsCoursesArticles />,
            title: isCondensedVersion
              ? VALUE_PROPS_TITLES.condensedMeditationsCoursesArticles
              : VALUE_PROPS_TITLES.meditationsCoursesArticles,
          },
        ]),
    {
      icon: isCondensedVersion ? <CheckMarkIcon /> : <ValuePropDedicatedSupport />,
      title: isCondensedVersion ? VALUE_PROPS_TITLES.condensedDedicatedSupport : VALUE_PROPS_TITLES.dedicatedSupport,
    },
    ...(isCondensedVersion
      ? []
      : [
          {
            icon: <ValuePropProvidersIntelligentlyMatched />,
            title: VALUE_PROPS_TITLES.providersIntelligentlyMatched,
          },
        ]),
  ]

  const iconSize = 56
  const lyraSelectValueProps = [
    {
      icon: <LyraSelectSelfCareValueSpotIllustration size={iconSize} />,
      title: VALUE_PROPS_TITLES_LYRA_SELECT.onDemandContent,
    },
    {
      icon: <LyraSelectPersonalizedCareIllustration size={iconSize} />,
      title: VALUE_PROPS_TITLES_LYRA_SELECT.personalizedCare,
    },
    {
      icon: <LyraSelectCareSessionIllustration size={iconSize} />,
      title: VALUE_PROPS_TITLES_LYRA_SELECT.mentalHealthSessions,
    },
    {
      icon: <LyraSelectCareNavigatorValuePropSpotIllustration size={iconSize} />,
      title: VALUE_PROPS_TITLES_LYRA_SELECT.dedicatedSupport,
    },
  ]

  const googleCriticalTalentProps = [
    {
      icon: <ValuePropMentalHealthSessions />,
      title: VALUE_PROPS_TITLES_GOOGLE_CRITICAL_TALENT.coachingSessions,
    },
    {
      icon: <ValuePropOnDemand />,
      title: VALUE_PROPS_TITLES_GOOGLE_CRITICAL_TALENT.onDemandContent,
    },
    {
      icon: <ValuePropDedicatedSupport />,
      title: VALUE_PROPS_TITLES_GOOGLE_CRITICAL_TALENT.dedicatedSupport,
    },
  ]

  if (isUserGoogleCriticalTalent) {
    return googleCriticalTalentProps
  }

  if (isLyraSelect) {
    return lyraSelectValueProps
  }

  return wfmhValueProps
}

export const RegistrationValueProps: React.FC<{
  isCondensedVersion?: boolean
  hideDependentField?: boolean
  libraryDisabled?: boolean
  isUserGoogleCriticalTalent?: boolean
  isAutoEnrollment?: boolean
  customerConfiguration?: string
  customerDisplayName?: string
  numberOfSessions?: string | number
  dualSessionEntitlementsEnabled?: boolean
}> = ({
  isCondensedVersion = false,
  hideDependentField = false,
  libraryDisabled = false,
  isUserGoogleCriticalTalent = false,
  isAutoEnrollment = false,
  customerConfiguration,
  customerDisplayName,
  numberOfSessions,
  dualSessionEntitlementsEnabled,
}) => {
  const { isMinWidthLaptop } = useMediaQuerySize()

  return (
    <>
      <ValuePropsHeader
        size={isCondensedVersion ? SubheadSize.LARGE : SubheadSize.MEDIUM}
        style={
          isCondensedVersion
            ? {
                textAlign: 'center',
                marginTop: isMinWidthLaptop && 48,
                marginBottom: 24,
              }
            : undefined
        }
        text={
          <FormattedMessage
            defaultMessage='What you get with Lyra'
            description='Header for value props on sign-up page'
          />
        }
        level={'2'}
      />
      <ValuePropsIconList
        items={getValuePropsItems(
          isCondensedVersion,
          hideDependentField,
          libraryDisabled,
          isUserGoogleCriticalTalent,
          isAutoEnrollment,
          customerConfiguration,
          customerDisplayName,
          numberOfSessions,
          dualSessionEntitlementsEnabled,
        )}
        isCondensedVersion={isCondensedVersion}
      />
    </>
  )
}
